import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getLandingPageTitleColor } from "../../functions/themeAssets";
import "../fonts/breuer-headline.css";
import "./wordSearchStage.css";
import "./crosswordStage.css";
import "./stageActions.css";
import Swal from "sweetalert2";
import {
  buildWordSearchGrid,
  cellKey,
  DEFAULT_GRID_SIZE,
  formatWordListLabel,
  getCellsOnPath,
  parseHintDisplayMode,
  parseWordPuzzleFromTheme,
} from "./wordSearchUtils";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";
import { showStageCompleteCelebration } from "../../functions/stageCelebration";
import {
  fireStageHelpSwal,
  getStageRulesFromTheme,
} from "../../functions/stageRules";

const PILL_COLORS = [
  "#b8e4f0",
  "#d4c4f0",
  "#c8b8f0",
  "#b8f0d8",
  "#c8e8c8",
  "#ffd8c8",
  "#f0c8e8",
  "#e8d8b8",
];

const FOUND_HIGHLIGHTS = [
  "rgba(245, 166, 35, 0.75)",
  "rgba(91, 192, 222, 0.75)",
  "rgba(155, 89, 182, 0.7)",
  "rgba(61, 187, 109, 0.75)",
  "rgba(255, 107, 90, 0.75)",
  "rgba(100, 149, 237, 0.75)",
  "rgba(255, 193, 7, 0.75)",
  "rgba(236, 112, 99, 0.75)",
];

function measureHighlightPillStyle(gridEl, wrapEl, cells) {
  if (!gridEl || !wrapEl || !cells.length) return null;

  const start = cells[0];
  const end = cells[cells.length - 1];
  const startEl = gridEl.querySelector(
    `[data-row="${start.row}"][data-col="${start.col}"]`
  );
  const endEl = gridEl.querySelector(
    `[data-row="${end.row}"][data-col="${end.col}"]`
  );
  if (!startEl || !endEl) return null;

  const wrapRect = wrapEl.getBoundingClientRect();
  const startRect = startEl.getBoundingClientRect();
  const endRect = endEl.getBoundingClientRect();

  const x0 = startRect.left + startRect.width / 2 - wrapRect.left;
  const y0 = startRect.top + startRect.height / 2 - wrapRect.top;
  const x1 = endRect.left + endRect.width / 2 - wrapRect.left;
  const y1 = endRect.top + endRect.height / 2 - wrapRect.top;

  const dx = x1 - x0;
  const dy = y1 - y0;
  const centerDistance = Math.sqrt(dx * dx + dy * dy);
  const angleRad = Math.atan2(dy, dx);
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const cellSize = (startRect.width + endRect.width) / 2;
  const pillThickness = Math.max(
    16,
    Math.round(((startRect.height + endRect.height) / 2) * 0.78)
  );
  const halfCell = cellSize / 2;
  const startX = x0 - halfCell * cos;
  const startY = y0 - halfCell * sin;

  return {
    left: startX,
    top: startY - pillThickness / 2,
    width: centerDistance + cellSize,
    height: pillThickness,
    transform: `rotate(${angleRad * (180 / Math.PI)}deg)`,
    transformOrigin: "0 50%",
  };
}

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Stage2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  /** Points earned in stage 2 this session (resumes from DB stage2 column). */
  const [stage2Points, setStage2Points] = useState(0);
  /** HUD total = other stages + stage2Points. */
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [foundWords, setFoundWords] = useState(() => new Set());
  const [activeCells, setActiveCells] = useState([]);
  const activeCellsRef = useRef([]);
  const [foundHighlights, setFoundHighlights] = useState([]);
  const [measuredPillStyles, setMeasuredPillStyles] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const gridRef = useRef(null);
  const gridWrapRef = useRef(null);
  const isDraggingRef = useRef(false);
  const startCellRef = useRef(null);
  const foundRef = useRef(new Set());
  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const stage2CompleteRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = storedUser.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const wordEntries = useMemo(
    () => parseWordPuzzleFromTheme(themeData),
    [themeData]
  );

  const hintMode = useMemo(
    () => parseHintDisplayMode(themeData),
    [themeData]
  );

  const gridSize = useMemo(() => {
    const maxLen = wordEntries.reduce(
      (m, e) => Math.max(m, e.word.length),
      0
    );
    return Math.max(DEFAULT_GRID_SIZE, maxLen + 1);
  }, [wordEntries]);

  const puzzle = useMemo(() => {
    if (!wordEntries.length) return null;
    return buildWordSearchGrid(
      wordEntries.map((e) => e.word),
      gridSize
    );
  }, [wordEntries, gridSize]);

  const targetWords = useMemo(
    () => new Set(wordEntries.map((e) => e.word)),
    [wordEntries]
  );

  const pointsPerWord = useMemo(() => {
    const p = Number(themeData?.points);
    return Number.isFinite(p) && p > 0 ? p : 10;
  }, [themeData?.points]);

  const title = useMemo(() => {
    const t =
      themeData?.wordpuzzle_title ||
      themeData?.landing_page_title ||
      themeData?.main_title ||
      themeData?.themename ||
      "Word Search";
    return String(t).trim();
  }, [themeData]);

  const subtitle = useMemo(() => {
    const custom = themeData?.wordpuzzle_subtitle;
    if (custom && String(custom).trim()) return String(custom).trim();
    return "Find the given words in the grid. (Words may be horizontal, vertical or diagonal)";
  }, [themeData]);

  const titleTextColor = useMemo(
    () => getLandingPageTitleColor(themeData),
    [themeData]
  );

  const themedTextStyle = useMemo(
    () => (titleTextColor ? { color: titleTextColor } : undefined),
    [titleTextColor]
  );

  useEffect(() => {
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [themeStatus, dispatch]);

  useEffect(() => {
    document.body.classList.add("word-search-page");
    return () => {
      document.body.classList.remove("word-search-page");
    };
  }, []);

  useEffect(() => {
    foundRef.current = foundWords;
  }, [foundWords]);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;

    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();

    const tick = setInterval(() => {
      const sessionSec = Math.floor(
        (Date.now() - sessionStartRef.current) / 1000
      );
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage2Points);
  }, [stage2Points]);

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      if (themeStatus !== "succeeded" || !puzzle) return;

      const uid =
        storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        let rowId = sessionStorage.getItem(STAGE_ROW_KEY);

        if (!rowId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: storedUser.name || "",
              user_type: storedUser.user_type || storedUser.userType || "",
            }),
          });
          const startData = await startRes.json().catch(() => ({}));
          if (startRes.ok && startData.success && startData.id != null) {
            rowId = String(startData.id);
            sessionStorage.setItem(STAGE_ROW_KEY, rowId);
          }
        }

        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        if (!cancelled) setStageRowId(rowId);

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));

        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st);
          const resumeRoute = getRedirectForPlayedStage(st, 2);
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage2 = Number(st.stage2) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage2);
          setStage2Points(dbStage2);
          setDisplayTotalScore(dbTotal);
        }

        setStageActive(2, storedUser);
        setDbReady(true);
      } catch (err) {
        console.error("loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [
    themeStatus,
    puzzle,
    backendBase,
    adminToken,
    isDemoBypass,
    storedUser,
    wordEntries.length,
    pointsPerWord,
    navigate,
  ]);

  const finishStage = useCallback(async (skip = false) => {
    if (submitting || submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);

    const uid =
      storedUser.userId || storedUser.userid || storedUser.id;
    const sessionId = storedUser.sessionId;
    const organizationId = storedUser.organizationId;
    const timeStr = formatSecondsToClock(elapsedRef.current);
    const stage2Score = skip ? 0 : stage2Points;
    const stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
    const finalTotal = otherStagesTotalRef.current + stage2Score;

    if (!isDemoBypass && backendBase && adminToken && stageId) {
      const saveResult = await saveGameStageAndReport({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId,
        stageNumber: 2,
        score: stage2Score,
        time: timeStr,
        total_score: finalTotal,
      });
      if (saveResult.total_score != null) {
        setDisplayTotalScore(saveResult.total_score);
      }
    }

    clearStageActive(2);
    markStageCompleted(2, storedUser);

    await showStageCompleteCelebration({
      stageNumber: 2,
      skip,
      score: stage2Score,
    });

    if (isDemoBypass) {
      window.location.href =
        String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
      return;
    }
    navigate("/stage3");
  }, [
    submitting,
    stage2Points,
    storedUser,
    isDemoBypass,
    backendBase,
    adminToken,
    stageRowId,
    navigate,
  ]);

  useEffect(() => {
    if (!dbReady || !wordEntries.length || submittedRef.current) return;
    if (stage2CompleteRef.current) return;
    if (foundWords.size >= wordEntries.length) {
      finishStage(false);
    }
  }, [dbReady, foundWords.size, wordEntries.length, finishStage]);

  const handleSkip = useCallback(() => {
    finishStage(true);
  }, [finishStage]);

  const markWordFound = useCallback(
    (word, cells) => {
      if (foundRef.current.has(word)) return;
      const colorIdx = foundRef.current.size % FOUND_HIGHLIGHTS.length;
      const color = FOUND_HIGHLIGHTS[colorIdx];

      setFoundWords((prev) => {
        const next = new Set(prev);
        next.add(word);
        return next;
      });
      setStage2Points((p) => p + pointsPerWord);
      setFoundHighlights((prev) => [...prev, { word, cells, color }]);
    },
    [pointsPerWord]
  );

  const resolveSelection = useCallback(
    (cells) => {
      if (!cells.length) return;
      const selected = cells
        .map(({ row, col }) => puzzle.grid[row]?.[col] || "")
        .join("");
      const reversed = [...selected].reverse().join("");

      let matched = null;
      if (targetWords.has(selected)) matched = selected;
      else if (targetWords.has(reversed)) matched = reversed;

      if (matched && !foundRef.current.has(matched)) {
        markWordFound(matched, cells);
      }
    },
    [puzzle, targetWords, markWordFound]
  );

  const updateActivePath = useCallback((endRow, endCol) => {
    const start = startCellRef.current;
    if (!start) return;
    const cells = getCellsOnPath(start, { row: endRow, col: endCol });
    activeCellsRef.current = cells;
    setActiveCells(cells);
  }, []);

  const onPointerDown = useCallback((row, col) => {
    isDraggingRef.current = true;
    startCellRef.current = { row, col };
    const cells = [{ row, col }];
    activeCellsRef.current = cells;
    setActiveCells(cells);
  }, []);

  const onPointerEnter = useCallback(
    (row, col) => {
      if (!isDraggingRef.current) return;
      updateActivePath(row, col);
    },
    [updateActivePath]
  );

  const onPointerUp = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    resolveSelection(activeCellsRef.current);
    activeCellsRef.current = [];
    setActiveCells([]);
    startCellRef.current = null;
  }, [resolveSelection]);

  const onGridTouchMove = useCallback(
    (e) => {
      if (!isDraggingRef.current) return;
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      const cell = el?.closest?.("[data-row][data-col]");
      if (cell?.dataset?.row != null) {
        onPointerEnter(Number(cell.dataset.row), Number(cell.dataset.col));
      }
    },
    [onPointerEnter]
  );

  useEffect(() => {
    const up = () => onPointerUp();
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [onPointerUp]);

  const remeasureHighlights = useCallback(() => {
    const grid = gridRef.current;
    const wrap = gridWrapRef.current;
    if (!grid || !wrap || !foundHighlights.length) {
      setMeasuredPillStyles({});
      return;
    }

    const next = {};
    foundHighlights.forEach(({ word, cells, color }) => {
      const pill = measureHighlightPillStyle(grid, wrap, cells);
      if (pill) next[word] = { ...pill, background: color };
    });
    setMeasuredPillStyles(next);
  }, [foundHighlights]);

  useLayoutEffect(() => {
    remeasureHighlights();
  }, [remeasureHighlights, gridSize]);

  useEffect(() => {
    const grid = gridRef.current;
    const wrap = gridWrapRef.current;
    if (!grid || !wrap) return;

    const observer = new ResizeObserver(remeasureHighlights);
    observer.observe(grid);
    observer.observe(wrap);
    window.addEventListener("orientationchange", remeasureHighlights);
    return () => {
      observer.disconnect();
      window.removeEventListener("orientationchange", remeasureHighlights);
    };
  }, [remeasureHighlights, puzzle, gridSize]);

  const showHelp = () => {
    fireStageHelpSwal({
      rules: getStageRulesFromTheme(themeData, "wordpuzzle_rules"),
      themeData,
    });
  };

  if (themeStatus === "loading" || themeStatus === "idle" || !dbReady) {
    return <div className="word-search-stage__loading">Loading game…</div>;
  }

  if (!wordEntries.length || !puzzle) {
    return (
      <div className="word-search-stage__empty">
        No word puzzle words configured. Add words in theme admin under Word Puzzle.
      </div>
    );
  }

  const activeSet = new Set(activeCells.map((c) => cellKey(c.row, c.col)));

  return (
    <div className="word-search-stage">
      <header className="word-search-stage__header">
        <div className="word-search-stage__title-block">
          <span className="word-search-stage__badge">STAGE 2</span>
          <h1 className="word-search-stage__title" style={themedTextStyle}>
            {title}
          </h1>
          <p className="word-search-stage__subtitle" style={themedTextStyle}>
            {subtitle}
          </p>
        </div>
        <div className="word-search-stage__hud">
          <div className="word-search-stage__stats-card">
            <div className="word-search-stage__stat">
              <span aria-hidden>⏱</span>
              <span>{formatSecondsToClock(elapsedSec)}</span>
            </div>
            <div className="word-search-stage__stat">
              <span aria-hidden>★</span>
              <span>{displayTotalScore}</span>
            </div>
          </div>
          <div className="stage-hud-actions">
            <button
              type="button"
              className="crossword-stage__help-btn"
              aria-label="How to play"
              onClick={showHelp}
            >
              ?
            </button>
            <button
              type="button"
              className="game-stage-actions__skip-btn game-stage-actions__skip-btn--hud"
              disabled={submitting}
              onClick={handleSkip}
            >
              Skip
            </button>
          </div>
        </div>
      </header>

      <div className="word-search-stage__body">
        <div className="word-search-stage__grid-panel">
          <div className="word-search-stage__grid-card">
            <div className="word-search-stage__grid-wrap" ref={gridWrapRef}>
              <div
                className="word-search-stage__highlights"
                aria-hidden
              >
                {foundHighlights.map(({ word }) => {
                  const pillStyle = measuredPillStyles[word];
                  if (!pillStyle) return null;
                  return (
                    <div
                      key={word}
                      className="word-search-stage__highlight-pill"
                      style={pillStyle}
                    />
                  );
                })}
              </div>
              <div
                ref={gridRef}
                className="word-search-stage__grid"
                style={{
                  gridTemplateColumns: `repeat(${gridSize}, auto)`,
                }}
                onMouseLeave={() => {
                  if (isDraggingRef.current) onPointerUp();
                }}
                onTouchMove={onGridTouchMove}
              >
              {puzzle.grid.map((row, r) =>
                row.map((letter, c) => {
                  const key = cellKey(r, c);
                  const isActive = activeSet.has(key);
                  return (
                    <div
                      key={key}
                      className={`word-search-stage__cell${
                        isActive ? " word-search-stage__cell--active" : ""
                      }`}
                      style={
                        titleTextColor ? { color: titleTextColor } : undefined
                      }
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onPointerDown(r, c);
                      }}
                      onMouseEnter={() => onPointerEnter(r, c)}
                      onTouchStart={(e) => {
                        e.preventDefault();
                        onPointerDown(r, c);
                      }}
                      data-row={r}
                      data-col={c}
                    >
                      {letter}
                    </div>
                  );
                })
              )}
              </div>
            </div>
          </div>
        </div>

        <div className="word-search-stage__word-list">
          {wordEntries.map((entry, i) => {
            const label = formatWordListLabel(entry, hintMode);
            if (!label) return null;
            const found = foundWords.has(entry.word);
            return (
              <div
                key={entry.word}
                className={`word-search-stage__word-pill${
                  found ? " word-search-stage__word-pill--found" : ""
                }`}
                style={{ background: PILL_COLORS[i % PILL_COLORS.length] }}
              >
                <span className="word-search-stage__word-pill-text">
                  {label}
                </span>
                {found && <span className="word-search-stage__check">✓</span>}
              </div>
            );
          })}
        </div>

        <footer className="word-search-stage__footer">
          <button
            type="button"
            className="word-search-stage__submit-btn"
            disabled={submitting}
            onClick={() => finishStage(false)}
          >
            SUBMIT
          </button>
        </footer>
      </div>

    </div>
  );
}
